/** The console's only boundary to browser-owned persistence. */

import type { SnapshotSlot } from "@sx/api-client";

import { recordFailure } from "@sx/observability/recorder";

type BrowserStorageArea = "persistent" | "tab";
type BrowserStorageOperation = "read" | "write" | "remove";
type BrowserStorageFailureReason = "quota" | "denied" | "corrupt" | "unknown";
export type TabStorageLocation = "session" | "memory" | "failed";

export interface BrowserStorageAreaStatus {
  readonly readable: boolean;
  readonly writable: boolean;
  readonly removable: boolean;
  readonly corrupt: boolean;
}

interface BrowserStorageStatus {
  readonly persistent: BrowserStorageAreaStatus;
  readonly tab: BrowserStorageAreaStatus;
}

export interface TabValue<T> {
  readonly value: T;
  readonly source: Exclude<TabStorageLocation, "failed">;
}

type StorageScope =
  | { readonly kind: "device" }
  | { readonly kind: "principal"; readonly principalId: string };

interface StoredEnvelope {
  readonly version: 1;
  readonly updatedAt: number;
  readonly value: unknown;
}

const STORAGE_VERSION = 1;
const TAB_MEMORY_LIMIT = 32;
const listeners = new Set<() => void>();
const corruptKeys: Record<BrowserStorageArea, Set<string>> = {
  persistent: new Set(),
  tab: new Set(),
};
const tabMemory = new Map<string, StoredEnvelope>();

const HEALTHY_AREA: BrowserStorageAreaStatus = {
  readable: true,
  writable: true,
  removable: true,
  corrupt: false,
};

const DEFAULT_STATUS: BrowserStorageStatus = {
  persistent: HEALTHY_AREA,
  tab: HEALTHY_AREA,
};
let status = DEFAULT_STATUS;

// Server rendering cannot observe browser capability or retain a person's tab data.
function hasBrowser(): boolean { return typeof window !== "undefined"; }

function sameArea(
  left: BrowserStorageAreaStatus,
  right: BrowserStorageAreaStatus,
): boolean {
  return (
    left.readable === right.readable &&
    left.writable === right.writable &&
    left.removable === right.removable &&
    left.corrupt === right.corrupt
  );
}

function publishArea(
  area: BrowserStorageArea,
  next: BrowserStorageAreaStatus,
): void {
  if (sameArea(status[area], next)) return;
  status = { ...status, [area]: next };
  listeners.forEach((listener) => listener());
}

function capability(
  operation: BrowserStorageOperation,
): keyof BrowserStorageAreaStatus {
  if (operation === "read") return "readable";
  if (operation === "write") return "writable";
  return "removable";
}

function reasonFor(
  error: unknown,
): Exclude<BrowserStorageFailureReason, "corrupt"> {
  if (typeof DOMException !== "undefined" && error instanceof DOMException) {
    if (
      error.name === "QuotaExceededError" ||
      error.name === "NS_ERROR_DOM_QUOTA_REACHED"
    ) {
      return "quota";
    }
    if (error.name === "SecurityError" || error.name === "NotAllowedError")
      return "denied";
  }
  return "unknown";
}

function failed(
  area: BrowserStorageArea,
  operation: BrowserStorageOperation,
  error: unknown,
): void {
  const field = capability(operation);
  const changed = status[area][field];
  publishArea(area, { ...status[area], [field]: false });
  if (changed) {
    // A browser refusing its own storage is a machine's fact, recorded where every other
    // console failure is. UNSET, not ERROR: a Safari private window is not this service
    // failing, and must not spend its error budget.
    recordFailure(
      "console.browser_storage_failure",
      { area, operation, reason: reasonFor(error) },
      { status: "unset" },
    );
  }
}

function succeeded(
  area: BrowserStorageArea,
  operation: BrowserStorageOperation,
): void {
  const field = capability(operation);
  publishArea(area, { ...status[area], [field]: true });
}

function markCorrupt(area: BrowserStorageArea, key: string): void {
  corruptKeys[area].add(key);
  const changed = !status[area].corrupt;
  publishArea(area, { ...status[area], corrupt: true });
  if (changed) {
    recordFailure(
      "console.browser_storage_failure",
      {
        area,
        operation: "read",
        reason: "corrupt" satisfies BrowserStorageFailureReason,
      },
      { status: "unset" },
    );
  }
}

function clearCorrupt(area: BrowserStorageArea, key: string): void {
  if (!corruptKeys[area].delete(key) || corruptKeys[area].size > 0) return;
  publishArea(area, { ...status[area], corrupt: false });
}

function scopeKey(scope: StorageScope): string {
  return scope.kind === "device" ? "device" : `principal:${scope.principalId}`;
}

export function browserStorageKey(scope: StorageScope, name: string): string {
  return `sx:v${STORAGE_VERSION}:${scopeKey(scope)}:${name}`;
}

function tabStorageKey(principalId: string, name: string): string {
  return `sx:v${STORAGE_VERSION}:principal:${principalId}:${name}`;
}

function isEnvelope(value: unknown): value is StoredEnvelope {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    candidate.version === STORAGE_VERSION &&
    typeof candidate.updatedAt === "number" &&
    "value" in candidate
  );
}

function parseEnvelope(raw: string): StoredEnvelope | undefined {
  try {
    const envelope: unknown = JSON.parse(raw);
    return isEnvelope(envelope) ? envelope : undefined;
  } catch {
    return undefined;
  }
}

export function readBrowserValue<T>(
  scope: StorageScope,
  name: string,
  parse: (value: unknown) => T | undefined,
): T | undefined {
  if (!hasBrowser()) return undefined;
  const key = browserStorageKey(scope, name);
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(key);
    succeeded("persistent", "read");
  } catch (error) {
    failed("persistent", "read", error);
    return undefined;
  }
  if (raw === null) {
    clearCorrupt("persistent", key);
    return undefined;
  }
  const envelope = parseEnvelope(raw);
  if (envelope === undefined) {
    markCorrupt("persistent", key);
    return undefined;
  }
  const parsed = parse(envelope.value);
  if (parsed === undefined) markCorrupt("persistent", key);
  else clearCorrupt("persistent", key);
  return parsed;
}

export function writeBrowserValue(
  scope: StorageScope,
  name: string,
  value: unknown,
): boolean {
  if (!hasBrowser()) return false;
  const key = browserStorageKey(scope, name);
  try {
    const envelope: StoredEnvelope = {
      version: STORAGE_VERSION,
      updatedAt: Date.now(),
      value,
    };
    window.localStorage.setItem(key, JSON.stringify(envelope));
    succeeded("persistent", "write");
    clearCorrupt("persistent", key);
    return true;
  } catch (error) {
    failed("persistent", "write", error);
    return false;
  }
}

export function removeBrowserValue(scope: StorageScope, name: string): boolean {
  if (!hasBrowser()) return false;
  const key = browserStorageKey(scope, name);
  try {
    window.localStorage.removeItem(key);
    succeeded("persistent", "remove");
    clearCorrupt("persistent", key);
    return true;
  } catch (error) {
    failed("persistent", "remove", error);
    return false;
  }
}

function removeStoredPrincipalValues(
  storage: Storage,
  prefix: string,
  area: BrowserStorageArea,
): boolean {
  try {
    const keys = Array.from({ length: storage.length }, (_, index) =>
      storage.key(index),
    ).filter((key): key is string => key !== null && key.startsWith(prefix));
    keys.forEach((key) => {
      storage.removeItem(key);
      clearCorrupt(area, key);
    });
    succeeded(area, "remove");
    return true;
  } catch (error) {
    failed(area, "remove", error);
    return false;
  }
}

export function clearPrincipalBrowserState(principalId: string): boolean {
  if (!hasBrowser()) return false;
  const prefix = `sx:v${STORAGE_VERSION}:principal:${principalId}:`;
  for (const key of tabMemory.keys()) {
    if (key.startsWith(prefix)) tabMemory.delete(key);
  }
  const persistentCleared = removeStoredPrincipalValues(
    window.localStorage,
    prefix,
    "persistent",
  );
  const tabCleared = removeStoredPrincipalValues(
    window.sessionStorage,
    prefix,
    "tab",
  );
  return persistentCleared && tabCleared;
}

export function readTabValue<T>(
  principalId: string,
  name: string,
  parse: (value: unknown) => T | undefined,
): TabValue<T> | undefined {
  if (!hasBrowser()) return undefined;
  const key = tabStorageKey(principalId, name);
  const fallback = tabMemory.get(key);
  if (fallback !== undefined) {
    const value = parse(fallback.value);
    if (value === undefined) {
      markCorrupt("tab", key);
      return undefined;
    }
    return { value, source: "memory" };
  }

  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(key);
    succeeded("tab", "read");
  } catch (error) {
    failed("tab", "read", error);
    return undefined;
  }
  if (raw === null) {
    clearCorrupt("tab", key);
    return undefined;
  }
  const envelope = parseEnvelope(raw);
  if (envelope === undefined) {
    markCorrupt("tab", key);
    return undefined;
  }
  const value = parse(envelope.value);
  if (value === undefined) {
    markCorrupt("tab", key);
    return undefined;
  }
  clearCorrupt("tab", key);
  return { value, source: "session" };
}

export function writeTabValue(
  principalId: string,
  name: string,
  value: unknown,
): TabStorageLocation {
  if (!hasBrowser()) return "failed";
  const key = tabStorageKey(principalId, name);
  const envelope: StoredEnvelope = {
    version: STORAGE_VERSION,
    updatedAt: Date.now(),
    value,
  };
  let serialized: string;
  try {
    serialized = JSON.stringify(envelope);
  } catch (error) {
    failed("tab", "write", error);
    return "failed";
  }
  try {
    window.sessionStorage.setItem(key, serialized);
    tabMemory.delete(key);
    succeeded("tab", "write");
    clearCorrupt("tab", key);
    return "session";
  } catch (error) {
    failed("tab", "write", error);
    if (!tabMemory.has(key) && tabMemory.size >= TAB_MEMORY_LIMIT)
      return "failed";
    tabMemory.set(key, envelope);
    return "memory";
  }
}

export function removeTabValue(principalId: string, name: string): boolean {
  if (!hasBrowser()) return false;
  const key = tabStorageKey(principalId, name);
  tabMemory.delete(key);
  try {
    window.sessionStorage.removeItem(key);
    succeeded("tab", "remove");
    clearCorrupt("tab", key);
    return true;
  } catch (error) {
    failed("tab", "remove", error);
    return false;
  }
}

/** One raw string slot, for the single consumer that owns its own envelope.
 *
 * The persisted query cache carries a build id, a save time, and the principal it belongs
 * to, and a snapshot that fails any of those checks is discarded rather than repaired — so
 * wrapping it in this module's versioned envelope would be a second version stamp saying
 * less than the first. What it does need is the rest of this boundary: one keying scheme,
 * and a quota or permission failure reported once instead of thrown into a render. */
export function browserStorageSlot(
  scope: StorageScope,
  name: string,
): SnapshotSlot {
  const key = browserStorageKey(scope, name);
  return {
    read: () => {
      if (!hasBrowser()) return null;
      try {
        const value = window.localStorage.getItem(key);
        succeeded("persistent", "read");
        return value;
      } catch (error) {
        failed("persistent", "read", error);
        return null;
      }
    },
    write: (value) => {
      if (!hasBrowser()) return false;
      try {
        window.localStorage.setItem(key, value);
        succeeded("persistent", "write");
        return true;
      } catch (error) {
        failed("persistent", "write", error);
        return false;
      }
    },
    clear: () => {
      if (!hasBrowser()) return;
      try {
        window.localStorage.removeItem(key);
        succeeded("persistent", "remove");
      } catch (error) {
        failed("persistent", "remove", error);
      }
    },
  };
}

/** Transitional reader used only by explicit migrations at this boundary. */
export function readLegacyBrowserValue(name: string): string | null {
  if (!hasBrowser()) return null;
  try {
    const value = window.localStorage.getItem(name);
    succeeded("persistent", "read");
    return value;
  } catch (error) {
    failed("persistent", "read", error);
    return null;
  }
}

export function removeLegacyBrowserValue(name: string): boolean {
  if (!hasBrowser()) return false;
  try {
    window.localStorage.removeItem(name);
    succeeded("persistent", "remove");
    return true;
  } catch (error) {
    failed("persistent", "remove", error);
    return false;
  }
}

export function browserValueIsCorrupt(
  scope: StorageScope,
  name: string,
): boolean {
  return hasBrowser() && corruptKeys.persistent.has(browserStorageKey(scope, name));
}

export function browserStorageSnapshot(): BrowserStorageStatus {
  return hasBrowser() ? status : DEFAULT_STATUS;
}

export function subscribeBrowserStorage(listener: () => void): () => void {
  if (!hasBrowser()) return () => {};
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Test isolation for this module's deliberately process-wide capability state. */
export function resetBrowserStorageForTests(): void {
  corruptKeys.persistent.clear();
  corruptKeys.tab.clear();
  tabMemory.clear();
  status = { persistent: HEALTHY_AREA, tab: HEALTHY_AREA };
}
